import { TransmissionStatus } from '@alberta/konexi-shared';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { getSelectionItems } from 'src/app/business/selectionitem-factory';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { OrderAttachmentListService } from 'src/app/shared/components/order-attachment-list/order-attachment-list.service';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { AttachmentTypeService } from 'src/app/shared/services/attachment/attachment-type.service';
import { TenantConfigService } from 'src/app/shared/services/tenant-config/tenant-config.service';

export enum ManualPrescriptionStatus {
  ToClarify = 'toClarify',
  Canceled = 'canceled',
  Faulty = 'faulty',
  CopyReceived = 'copyReceived',
  OriginalReceived = 'originalReceived',
}

@Component({
  selector: 'itl-single-order-prescription-status',
  templateUrl: './single-order-prescription-status.component.html',
  providers: [OrderAttachmentListService],
})
export class SingleOrderPrescriptionStatusComponent implements OnInit {
  @Input() public prescriptionStatus: TransmissionStatus;
  @Input() public patientId: string;
  @Input() public singleOrderId: string;
  public prescriptionStatusTypes: SelectionItem[];
  public prescriptionStatusType: AbstractControl;
  public form: UntypedFormGroup;
  public warningType = InfoBoxType.Warning;

  private _requireAttachmentForCopyReceived = false;
  private _prescriptionCopyAttachmentTypeKey: number | undefined;
  private _validationRunId = 0;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _translateService: TranslateService,
    private _modalController: ModalController,
    private _tenantConfigService: TenantConfigService,
    private _attachmentTypeService: AttachmentTypeService,
    private _orderAttachmentListService: OrderAttachmentListService
  ) {}

  public async ngOnInit() {
    this.initializeForm();
    this.prescriptionStatusTypes = getSelectionItems(
      ManualPrescriptionStatus,
      'singleOrder.prescriptionStatusType.',
      this._translateService
    );

    this._requireAttachmentForCopyReceived =
      await this._tenantConfigService.requireAttachmentForPrescriptionStatusCopyReceived();
    if (this._requireAttachmentForCopyReceived) {
      this._prescriptionCopyAttachmentTypeKey =
        await this._attachmentTypeService.getAttachmentTypeKey('PrescriptionCopy');
    }

    this.prescriptionStatusType.valueChanges.subscribe(value => {
      this.prescriptionStatus = value;
      this.validateCopyReceivedSelection();
    });

    await this.validateCopyReceivedSelection();
  }

  private initializeForm() {
    this.form = this._formBuilder.group({
      prescriptionStatus: [this.prescriptionStatus],
    });
    this.prescriptionStatusType = this.form.controls['prescriptionStatus'];
  }

  private async validateCopyReceivedSelection(): Promise<void> {
    // Guard against race conditions: if the user changes the selection quickly, an older async request
    // (attachment lookup) might resolve after a newer one. We ignore "stale" results by only applying
    // the result of the latest validation run.
    const runId = ++this._validationRunId;

    // Branch 1: feature disabled -> validation is not active for any status.
    if (!this._requireAttachmentForCopyReceived) {
      this.setFormError('requirePrescriptionCopyAttachment', false);
      return;
    }

    // Branch 2: validation only applies for "CopyReceived".
    if (this.prescriptionStatus !== TransmissionStatus.CopyReceived) {
      this.setFormError('requirePrescriptionCopyAttachment', false);
      return;
    }

    // Branch 3: missing context -> we cannot verify attachments, so we must block saving.
    if (!this.patientId || !this.singleOrderId || this._prescriptionCopyAttachmentTypeKey == null) {
      this.setFormError('requirePrescriptionCopyAttachment', true);
      return;
    }

    const total = await this._orderAttachmentListService.getTotal(
      this.patientId,
      this.singleOrderId,
      'singleOrderId',
      this._prescriptionCopyAttachmentTypeKey
    );

    if (runId !== this._validationRunId) {
      return;
    }

    this.setFormError('requirePrescriptionCopyAttachment', total <= 0);
  }

  private setFormError(key: string, active: boolean): void {
    // We intentionally set/remove errors on the FormGroup, because the wizard uses `form.valid`
    // and renders all validation messages centrally based on `form.errors`.
    const currentErrors = this.form.errors || {};

    if (active) {
      if (currentErrors[key]) {
        return;
      }
      this.form.setErrors({
        ...currentErrors,
        [key]: true,
      });
      return;
    }

    if (!currentErrors[key]) {
      return;
    }

    delete currentErrors[key];
    // Avoid unnecessary setErrors calls: only update if the error structure actually changes.
    if (Object.keys(currentErrors).length === 0) {
      this.form.setErrors(null);
      return;
    }
    this.form.setErrors({ ...currentErrors });
  }

  public async save() {
    await this._modalController.dismiss(this.prescriptionStatus).catch(error => console.log(error));
  }
  public async cancel() {
    await this._modalController.dismiss().catch(error => console.log(error));
  }
}
